'use client'

import type { Didomi } from '@/util/analytics'

import { observeGppReady } from './gpp'
import { observeTcfReady } from './tcf'

export type FreestarPrivacyFramework = 'none' | 'gpp' | 'tcf'

type PrivacyTarget = Pick<Window, '__gpp' | '__tcfapi'>

export type FreestarPrivacyObserver = {
    ready: Promise<void>
    recheck: () => void
}

export function selectFreestarPrivacyFramework(didomi: Didomi): FreestarPrivacyFramework {
    let regulation: string | undefined
    try {
        regulation = didomi.getCurrentUserStatus?.().regulation
    } catch {
        return 'gpp'
    }

    switch (regulation?.trim().toUpperCase()) {
        case 'GDPR':
            return 'tcf'
        case 'NONE':
            return 'none'
        default:
            return 'gpp'
    }
}

export function observeFreestarPrivacyReady(didomi: Didomi, target: PrivacyTarget = window): FreestarPrivacyObserver {
    const framework = selectFreestarPrivacyFramework(didomi)
    if (framework === 'tcf') {
        const observer = observeTcfReady(target)
        return { ready: observer.ready, recheck: observer.recheck }
    }

    if (framework === 'none') {
        return { ready: Promise.resolve(), recheck: () => undefined }
    }

    const observer = observeGppReady(target)
    return { ready: observer.ready.then(() => undefined), recheck: observer.recheck }
}

export function waitForFreestarPrivacyReady(didomi: Didomi, target: PrivacyTarget = window): Promise<void> {
    return observeFreestarPrivacyReady(didomi, target).ready
}
